/**
 * @fileoverview Local database schema version constants — the single source of
 *               truth for expected Dexie schema versions and minimum migratable
 *               versions across all three FireSquirrel web apps.
 * @module sync-config/local-db-versions
 *
 * Responsibilities:
 * - Define `EXPECTED_DB_SCHEMA_VERSION` and `MIN_MIGRATABLE_VERSION` for each
 *   app (system-web, tenant-web, mobile-pwa).
 * - Provide a lookup helper (`getLocalDbSchemaVersions`) that returns the
 *   constants for a given dbId.
 *
 * Dependencies:
 * - None — pure constants.
 *
 * Architecture:
 * - This file is the SINGLE SOURCE OF TRUTH for schema version constants.
 * - The frontend DB files (`web/packages/local-db/src/databases/*.ts`) re-export
 *   these constants so the localDatabaseManager can use them.
 * - The system-api imports these constants to seed the `local_db_metadata`
 *   MongoDB collection on startup and to run the authoritative
 *   `determineRecoveryAction()` logic in the validate endpoint.
 * - This package is the shared configuration layer — both API (Node.js) and
 *   web (Vite/ESM) import from it, eliminating drift between the two.
 *
 * Notes:
 * - When a developer bumps a Dexie schema version, they MUST update the
 *   constant here AND add the `db.version(N)` call in the corresponding
 *   frontend DB file. The local-db-schema-update skill enforces this.
 * - `MIGRATION_REGISTRY` is NOT shared here — it is frontend-only (the API
 *   does not need to know about `holdWrites` policies; that is a frontend
 *   execution concern).
 */

// ---------------------------------------------------------------------------
// DbId type — maintains the frontend DbId shape without importing from local-db
// ---------------------------------------------------------------------------

/**
 * @contract LocalDbId
 *
 * Purpose:
 * - Identifies which FireSquirrel web app owns a local database.
 *
 * Fields:
 * - 'system-web' | 'tenant-web' | 'mobile-pwa'
 *
 * Rules:
 * - Must stay aligned with the `DbId` type in `web/packages/local-db/src/types/dbMeta.ts`.
 * - Duplicated here (not imported) so this package has no dependency on the
 *   web-only local-db package.
 *
 * Sync:
 * - Not synced — used as a document key in `local_db_metadata`.
 */
export type LocalDbId = 'system-web' | 'tenant-web' | 'mobile-pwa';

// ---------------------------------------------------------------------------
// Schema version constants — one pair per app
// ---------------------------------------------------------------------------

/**
 * The current Dexie schema version that the system-web code expects.
 * When a developer adds a new `db.version(N)` in systemDb.ts, they MUST
 * update this constant to match.
 */
export const SYSTEM_EXPECTED_DB_SCHEMA_VERSION = 33;

/**
 * The oldest schema version for which system-web migration scripts exist.
 * If the local DB is older than this, the manager wipes and rebuilds instead
 * of attempting incremental migration.
 */
export const SYSTEM_MIN_MIGRATABLE_VERSION = 17;

/**
 * The current Dexie schema version that the tenant-web code expects.
 * When a developer adds a new `db.version(N)` in tenantDb.ts, they MUST
 * update this constant to match.
 */
// Merge of complex-inspections (v44-v46) + inspection-scheduling (v47-v49,
// re-based from dev's v44-v46) schema chains. Both branches had bumped
// v43->v44 for different tables, so dev's chain is appended as v47-v49.
// v50 restores the `cases` indexes (isArchived + complex visit multi-entry)
// that v48's redeclaration dropped.
export const TENANT_EXPECTED_DB_SCHEMA_VERSION = 58;

/**
 * The oldest schema version for which tenant-web migration scripts exist.
 * v15 added `_sync_meta`; migrations from v15 onward are supported.
 * Versions before v15 used encryption and are not migratable.
 */
export const TENANT_MIN_MIGRATABLE_VERSION = 15;

/**
 * The current Dexie schema version that the mobile-pwa code expects.
 * When a developer adds a new `dexie.version(N)` in mobileDb.ts, they MUST
 * update this constant to match.
 */
// Merge of complex-inspections (v15-v18) + inspection-scheduling (v19:
// inspection_programs table), public-safety-surveys (v20), local-only dirty
// ledger (v21), inspection delivery records (v22), and department-wide
// inspection locks (v23).
export const MOBILE_EXPECTED_DB_SCHEMA_VERSION = 24;

/**
 * The oldest schema version for which mobile-pwa migration scripts exist.
 * v5 added `_sync_meta`; migrations from v5 onward are supported.
 * Versions before v5 used encryption and are not migratable.
 */
export const MOBILE_MIN_MIGRATABLE_VERSION = 5;

// ---------------------------------------------------------------------------
// Lookup helper — returns the constants for a given dbId
// ---------------------------------------------------------------------------

/**
 * @contract LocalDbSchemaVersions
 *
 * Purpose:
 * - Bundles the expected and minimum migratable schema versions for one app.
 *
 * Fields:
 * - `expectedSchemaVersion`: The current Dexie version the code expects.
 * - `minMigratableVersion`: The oldest version with migration scripts.
 *
 * Rules:
 * - Returned by `getLocalDbSchemaVersions(dbId)`.
 *
 * Sync:
 * - Not synced — compiled-in constants.
 */
export interface LocalDbSchemaVersions {
  expectedSchemaVersion: number;
  minMigratableVersion: number;
}

/**
 * Returns the schema version constants for the given dbId.
 * Throws if the dbId is not recognized.
 *
 * @param dbId - 'system-web' | 'tenant-web' | 'mobile-pwa'
 * @returns The expected and minimum migratable schema versions.
 * @process getLocalDbSchemaVersions
 * Steps: Apply get local db schema versions using its declared inputs and shared helpers.
 * Inputs: dbId
 * Outputs: LocalDbSchemaVersions
 * Failure Handling: Propagates failures through the declared return or exception contract.
 * Idempotency: REQUIRED — repeated calls with the same inputs produce the same result.
 * @errorHandling getLocalDbSchemaVersions
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 */
export function getLocalDbSchemaVersions(dbId: LocalDbId): LocalDbSchemaVersions {
  switch (dbId) {
    case 'system-web':
      return {
        expectedSchemaVersion: SYSTEM_EXPECTED_DB_SCHEMA_VERSION,
        minMigratableVersion: SYSTEM_MIN_MIGRATABLE_VERSION,
      };
    case 'tenant-web':
      return {
        expectedSchemaVersion: TENANT_EXPECTED_DB_SCHEMA_VERSION,
        minMigratableVersion: TENANT_MIN_MIGRATABLE_VERSION,
      };
    case 'mobile-pwa':
      return {
        expectedSchemaVersion: MOBILE_EXPECTED_DB_SCHEMA_VERSION,
        minMigratableVersion: MOBILE_MIN_MIGRATABLE_VERSION,
      };
    default:
      throw new Error(`Unknown LocalDbId: ${dbId as string}`);
  }
}
